import { Controller } from '@hotwired/stimulus';

// Selects and copies `sourceTarget` as rich HTML (not plain text), matching
// what a manual select-all-and-copy would produce.
export default class extends Controller {
  static targets = ['source', 'copyButton', 'copiedButton'];
  static values = { trackCopyUrl: String };

  disconnect() {
    clearTimeout(this.resetButtonTimeoutId);
  }

  async copy() {
    await this.writeToClipboard();

    if (this.trackCopyUrlValue) {
      fetch(this.trackCopyUrlValue, { method: 'POST', keepalive: true }).catch(
        () => {
          // Analytics beacon — a failure here shouldn't affect the copy action.
        },
      );
    }

    this.flashCopied();
  }

  async writeToClipboard() {
    if (navigator.clipboard?.write && window.ClipboardItem) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([this.cleanHtml()], { type: 'text/html' }),
            'text/plain': new Blob([this.sourceTarget.innerText], {
              type: 'text/plain',
            }),
          }),
        ]);
        return;
      } catch {
        // Fall through to the plain-text fallback below (e.g. permission denied,
        // or a browser that supports the Clipboard API but not this MIME combination).
      }
    }
    await navigator.clipboard?.writeText(this.sourceTarget.innerText);
  }

  // The template's indentation renders as literal whitespace text nodes around tags.
  // Browsers collapse those visually, but some paste targets (Word, Outlook) render
  // them as visible blank space, so strip it before copying. Only whitespace runs that
  // contain a newline are touched — those are always template indentation, never a
  // meaningful single space between inline content.
  cleanHtml() {
    return this.sourceTarget.innerHTML
      .replace(/>\s*\n\s*/g, '>')
      .replace(/\s*\n\s*</g, '<')
      .trim();
  }

  flashCopied() {
    this.copyButtonTarget.classList.add('invisible');
    this.copiedButtonTarget.classList.remove('invisible');
    this.resetButtonTimeoutId = setTimeout(() => {
      this.copiedButtonTarget.classList.add('invisible');
      this.copyButtonTarget.classList.remove('invisible');
    }, 2000);
  }
}
